import { useQuery } from "@tanstack/react-query";
import { client } from "lib/sanity";
import { Faq, ShopPage, ShopPagePhaseTwo } from "lib/sanity.types";

interface QueryResult<T> {
  ms: number;
  query: string;
  result: T[];
}

export const useFetchShopPageQuery = ({ slug }: { slug: string }) =>
  useQuery(["fetchShopPage"], () =>
    client
      .fetch(`*[_type == "shopPages" && slug.current == "${slug}"]`)
      .then((shopPages: QueryResult<ShopPage>) => {
        if (shopPages.result.length === 0) return null;
        return shopPages.result[0];
      })
  );

export const useFetchShopPagePhaseTwoQuery = ({ slug }: { slug: string }) =>
  useQuery(["fetchShopPagePhaseTwo"], () =>
    client
      .fetch(`*[_type == "shopPagesPhaseTwo" && slug.current == "${slug}"]`)
      .then((shopPages: QueryResult<ShopPagePhaseTwo>) => {
        if (shopPages.result.length === 0) return null;
        return shopPages.result[0];
      })
  );

export const useFetchFaqsQuery = ({
  slug,
  type = "shopPages",
}: {
  slug: string;
  type?: "shopPages" | "shopPagesPhaseTwo";
}) =>
  useQuery(["fetchFaqs"], async () => {
    const faqsUrls = await client.fetch(
      `*[_type == "${type}" && slug.current == "${slug}"] {
          "faqsUrl": faqs.asset->url
         }`
    );
    if (faqsUrls.result.length === 0) return null;

    const url = faqsUrls.result[0].faqsUrl;
    if (url === null) return [] as Faq[];

    const faqs = await (await fetch(url)).json();
    return faqs as Faq[];
  });

interface AgreementDetails {
  name: string;
  url: string;
  _key: string;
}

interface Agreements {
  agreementDetails: AgreementDetails[];
  tenant: string;
}

export const useFetchAgreementsQuery = (tenant: string) => {
  return useQuery(["fetchAgreements"], async () => {
    const response: QueryResult<Agreements> = await client.fetch(
      `*[_type == "agreements" && tenant == "${tenant}"]`
    );
    return response.result[0] || null;
  });
};
